import { useId } from 'react';
import type { ChatSummary, Me } from '../api';
import { projectUrl, SECTIONS, type Section } from '../sections';
import styles from '../styles/app.module.css';
import { sectionName, TEXT } from '../texts';
import { inApp } from './AppLink';
import { Avatar } from './Avatar';
import { Brand } from './Brand';
import { PlusIcon, SectionIcon } from './icons';
import { ProjectSelect } from './ProjectSelect';

interface SidebarProps {
  me: Me;
  projectId: string;
  onProject: (projectId: string) => void;
  section: Section;
  onSection: (section: Section) => void;
  chats: ChatSummary[];
  activeChatId: string | null;
  onOpen: (chatId: string) => void;
  onNew: () => void;
  /** Counts shown next to a section, e.g. automations that need the consultant's attention. */
  badges?: Partial<Record<Section, number>>;
}

export function Sidebar({ me, projectId, onProject, section, onSection, chats, activeChatId, onOpen, onNew, badges = {} }: SidebarProps): React.JSX.Element {
  const recentId = useId();
  return (
    <aside className={styles.sidebar}>
      <Brand />
      <ProjectSelect projects={me.projects} value={projectId} onChange={onProject} label={TEXT.customer} />
      <button type="button" className={styles.newChat} onClick={onNew}>
        <PlusIcon />
        {TEXT.newChat}
      </button>
      <nav className={styles.nav} aria-label={TEXT.sections}>
        {SECTIONS.map((item) => (
          <a
            key={item}
            className={styles.navItem}
            href={projectUrl(projectId, item)}
            aria-current={item === section ? 'page' : undefined}
            onClick={(event) => {
              if (!inApp(event)) return;
              event.preventDefault();
              onSection(item);
            }}
          >
            <SectionIcon section={item} />
            {sectionName[item]}
            {(badges[item] ?? 0) > 0 && (
              <span className={styles.badge} aria-label={TEXT.attentionBadge(badges[item] ?? 0)}>
                {badges[item]}
              </span>
            )}
          </a>
        ))}
      </nav>
      <section className={styles.recent} aria-labelledby={recentId}>
        <h2 id={recentId} className={styles.label}>
          {TEXT.recentChats}
        </h2>
        {chats.length === 0 ? (
          <p className={styles.muted}>{TEXT.noChats}</p>
        ) : (
          <ul className={styles.chats}>
            {chats.map((chat) => (
              <li key={chat.id}>
                <button
                  type="button"
                  className={styles.chat}
                  aria-current={section === 'chat' && chat.id === activeChatId}
                  title={chat.title}
                  onClick={() => onOpen(chat.id)}
                >
                  {chat.title}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <div className={styles.person}>
        <Avatar name={me.name} email={me.email} />
        <span className={styles.name} title={me.name}>
          {me.name}
        </span>
        <a href="/auth/logout">{TEXT.signOut}</a>
      </div>
    </aside>
  );
}
